'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';

interface Recommendation {
  content_type: string;
  content_slug: string;
  score: number;
  reason: string;
}

interface RecommendationsData {
  recommendations: Recommendation[];
  based_on: {
    saved_tags: string[];
    recent_tags: string[];
    preferred_topics: string[];
  };
  tag_weights: [string, number][];
}

export default function RecommendationsSection() {
  const [data, setData] = useState<RecommendationsData | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    async function fetchRecommendations() {
      try {
        const response = await fetch('/api/secure/recommendations', {
          credentials: 'include'
        });

        if (response.ok) {
          const result = await response.json();
          setData(result.data);
        }
      } catch (error) {
        console.error('Failed to load recommendations:', error);
      } finally {
        setIsLoading(false);
      }
    }

    fetchRecommendations();
  }, []);

  const getContentTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      insight: 'Insight',
      insights: 'Insights',
      framework: 'Framework',
      frameworks: 'Frameworks',
      comparison: 'Comparison',
      comparisons: 'Comparisons',
      code: 'Code Sample',
      architecture: 'Architecture',
      architectures: 'Architectures',
      template: 'Template',
      templates: 'Templates',
      'case-study': 'Case Study',
      'case-studies': 'Case Studies'
    };
    return labels[type] || type;
  };

  const getContentUrl = (type: string, slug: string) => {
    // Handle pluralization for URLs
    if (type === 'case-study') return `/case-studies/${slug}`;
    // Pluralize if needed
    const urlType = type.endsWith('s') ? type : `${type}s`;
    return `/${urlType}/${slug}`;
  };

  const formatTitle = (slug: string) => {
    return slug.split('-').map((word: string) =>
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  if (isLoading) {
    return (
      <div className="bg-surface border border-border rounded-lg p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-accent-muted rounded w-48 mb-4"></div>
          <div className="space-y-3">
            <div className="h-20 bg-accent-muted rounded"></div>
            <div className="h-20 bg-accent-muted rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!data || data.recommendations.length === 0) {
    return null; // Don't show section if no recommendations
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-2xl font-bold text-foreground">
          Recommended for You
        </h2>
      </div>

      {/* Show what recommendations are based on */}
      {data.tag_weights && data.tag_weights.length > 0 && (
        <div className="mb-4 text-sm text-foreground-tertiary">
          Based on: {data.tag_weights.slice(0, 3).map(([tag]) => tag).join(', ')}
        </div>
      )}

      <div className="space-y-4">
        {data.recommendations.slice(0, 5).map((rec, index) => (
          <Link
            key={`${rec.content_type}-${rec.content_slug}-${index}`}
            href={getContentUrl(rec.content_type, rec.content_slug)}
            className={`block bg-surface border border-border rounded-lg p-5 hover:border-border-muted0 transition-all hover:scale-[1.02] hover-lift animate-slide-up stagger-${index + 1}`}
          >
            <div className="flex items-start justify-between mb-2">
              <span className="text-xs uppercase tracking-wider text-accent">
                {getContentTypeLabel(rec.content_type)}
              </span>
              <span className="text-xs text-foreground-tertiary">
                {rec.score}% match
              </span>
            </div>
            <h3 className="text-lg font-semibold text-foreground mb-1">
              {formatTitle(rec.content_slug)}
            </h3>
            <p className="text-sm text-foreground-tertiary">
              {rec.reason}
            </p>
          </Link>
        ))}
      </div>

      {/* Improve recommendations CTA */}
      <div className="mt-6 p-4 bg-accent-muted border border-border rounded-lg text-center">
        <p className="text-sm text-foreground-secondary mb-2">
          Want better recommendations?
        </p>
        <Link
          href="/dashboard/settings"
          className="text-accent hover:text-foreground transition-colors text-sm font-medium"
        >
          Update your topic preferences →
        </Link>
      </div>
    </div>
  );
}
